import type React from 'react';

import isEqual from 'lodash/isEqual';
import { createActionsHook, createStore, type StoreActionApi } from 'react-sweet-state';

import { type IconProps } from '@atlaskit/icon/types';
import {
	type ConversationAction,
	type CustomFollowUpContext,
} from '@atlassian/conversation-assistant';
import { type ProfileData } from '@atlassian/conversation-assistant-store';

//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
export type ViewConfig = {
	headerRightAlignedElement?: React.ReactElement;
	render: () => React.ReactElement;
};
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
export type AIActionStatus = 'beta' | 'new';
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
// - platform/packages/ai-mate/rovo-conversation-actions/src/common/types.ts
export type AIActionType =
	| 'ai-action'
	| 'agent-action'
	| 'studio-access'
	| 'convo-starter-action'
	| 'help-action';
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
type AISource = {
	sourceId: string;
};
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
export type BaseAIAction = {
	children?: Array<BaseAIAction>;
	closePanelOnSelection?: boolean;
	customFollowUpContext?: CustomFollowUpContext;
	description?: string | React.ReactElement;
	icon: React.ReactElement;
	id: string;
	onClick?: () => void;
	ref?: React.Ref<HTMLButtonElement>;
	status?: AIActionStatus;
	title: string;
	type?: AIActionType;
	viewConfig?: ViewConfig;
};
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
/**
 * Right now we are using the same AIAction type for actions, suggestedActions and nudges
 * but in the future we might want to have different types for each of them.
 */
export type AIAction = BaseAIAction &
	AISource & {
		children?: Array<AIAction>;
	};
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
export type BaseNudge = BaseAIAction & {
	nudgeIcon: (props: IconProps) => React.JSX.Element | null;
};
//Below type is copied to;
// - confluence/next/packages/object-sidebar-api/src/useObjectSidebar.tsx
export type Nudge = BaseNudge &
	AISource & {
		children?: Array<AIAction>;
	};
/**
 * AI Features will register their features here if they want to render their
 * AI Feature inside the Dynamic Surface Panel
 */
export enum DynamicSurfaceView {
	Default = 'default',
}

export type DynamicSurfaceSelectors = {
	actionsSelector?: (actions: AIAction[], context?: AISmartButtonContext) => AIAction[];
	nudgesSelector?: (nudges: Nudge[], context?: AISmartButtonContext) => Nudge[];
	suggestedActionsSelector?: (
		suggestedActions: AIAction[],
		context?: AISmartButtonContext,
	) => AIAction[];
};

export type EditorAISmartButtonReference = {
	onRemove: () => void;
	reference: string;
};

export type BaseAISmartButtonContext = EditorAISmartButtonReference;
export type AISmartButtonContext = BaseAISmartButtonContext & AISource;

export type SurfaceMetadata = {
	contentType: string;
	pageMode?: string;
	product: string;
	siteId: string;
};

export type BaseAutomationsConfig = {
	adminAutomations?: {
		onClick: () => void;
	};
	userAutomations?: {
		render: () => React.ReactElement;
	};
};
export type AutomationsConfig = BaseAutomationsConfig & AISource;

export type AISmartButtonState = {
	actions: AIAction[];
	automationsConfig?: AutomationsConfig;
	context?: AISmartButtonContext;
	currentView: DynamicSurfaceView | AIAction;
	nudges: Nudge[];
	nudgesRefreshed?: ConversationAction[];
	profileData: ProfileData;
	selectors?: DynamicSurfaceSelectors;
	showConvoStarters?: boolean;
	suggestedActions: AIAction[];
	surfaceMetadata: SurfaceMetadata;
};

export const initialState: AISmartButtonState = {
	suggestedActions: [],
	actions: [],
	nudges: [],
	currentView: DynamicSurfaceView.Default,
	surfaceMetadata: {
		siteId: '',
		product: '',
		contentType: '',
	},
	profileData: {
		name: '',
		displayName: '',
		avatarUrl: '',
	},
};

/**
 * Adds source ID to each action in the array recursively.
 */
const addSource = (actions: BaseAIAction[] | BaseNudge[], sourceId: string): AIAction[] | Nudge[] =>
	actions.map((action) => ({
		...action,
		sourceId,
		children: action.children ? addSource(action.children, sourceId) : undefined,
	}));

/**
 * Updates state only if the new state is different from the current state.
 */
const updateStateIfChanged = (
	newPartialState: Partial<AISmartButtonState>,
	getState: () => AISmartButtonState,
	setState: (state: Partial<AISmartButtonState>) => void,
) => {
	const currentState = getState();
	const stateChanged = Object.entries(newPartialState).some(
		([key, value]) => !isEqual(currentState[key as keyof AISmartButtonState], value),
	);
	if (stateChanged) {
		setState(newPartialState);
	}
};

/**
 * Updates a collection of items (actions, nudges, or suggestedActions) for a specific source.
 * Only updates if the items have changed to avoid unnecessary re-renders.
 */
const updateItemsForSource = <K extends 'actions' | 'nudges' | 'suggestedActions'>(
	sourceId: string,
	newItems: BaseAIAction[] | BaseNudge[],
	stateKey: K,
	getState: () => AISmartButtonState,
	setState: (state: Partial<AISmartButtonState>) => void,
) => {
	const currentItems = getState()[stateKey];
	const currentSourceItems = currentItems.filter((item) => item.sourceId === sourceId);

	const itemsMatch =
		currentSourceItems.length === newItems.length &&
		currentSourceItems.every((item, index) => isEqual(item, newItems[index]));

	if (itemsMatch) {
		return;
	}

	// Filter out items from this source and add new ones.
	const otherItems = currentItems.filter((item) => item.sourceId !== sourceId);
	const updatedItems = [...otherItems, ...addSource(newItems, sourceId)];

	setState({ [stateKey]: updatedItems });
};

export const actions = {
	setActions:
		(sourceId: string, actions: BaseAIAction[]) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateItemsForSource(sourceId, actions, 'actions', getState, setState);
		},
	setNudges:
		(sourceId: string, nudges: BaseNudge[]) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateItemsForSource(sourceId, nudges, 'nudges', getState, setState);
		},
	setNudgesRefreshed:
		(nudgesRefreshed: ConversationAction[]) =>
		({ setState }: StoreActionApi<AISmartButtonState>) => {
			setState({ nudgesRefreshed });
		},
	setSuggestedActions:
		(sourceId: string, suggestedActions: BaseAIAction[]) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateItemsForSource(sourceId, suggestedActions, 'suggestedActions', getState, setState);
		},
	removeAll:
		(sourceId?: string) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			const { actions, nudges, suggestedActions } = getState();

			if (sourceId) {
				const newState = {
					suggestedActions: suggestedActions.filter((action) => action.sourceId !== sourceId),
					actions: actions.filter((action) => action.sourceId !== sourceId),
					nudges: nudges.filter((nudge) => nudge.sourceId !== sourceId),
				};
				updateStateIfChanged(newState, getState, setState);
			} else {
				const newState = {
					suggestedActions: [],
					actions: [],
					nudges: [],
				};
				updateStateIfChanged(newState, getState, setState);
			}
		},
	changeView:
		(view: DynamicSurfaceView | AIAction | Nudge) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateStateIfChanged({ currentView: view }, getState, setState);
		},
	setSelectors:
		(selectors: DynamicSurfaceSelectors) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			const existingSelectors = getState().selectors;
			const newSelectors = {
				selectors: {
					actionsSelector:
						'actionsSelector' in selectors
							? selectors.actionsSelector
							: existingSelectors?.actionsSelector,
					suggestedActionsSelector:
						'suggestedActionsSelector' in selectors
							? selectors.suggestedActionsSelector
							: existingSelectors?.suggestedActionsSelector,
					nudgesSelector:
						'nudgesSelector' in selectors
							? selectors.nudgesSelector
							: existingSelectors?.nudgesSelector,
				},
			};
			updateStateIfChanged(newSelectors, getState, setState);
		},
	setContext:
		(context: AISmartButtonContext) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateStateIfChanged({ context }, getState, setState);
		},
	removeContext:
		(sourceId: AISmartButtonContext['sourceId']) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			const { context } = getState();
			if (context?.sourceId === sourceId) {
				updateStateIfChanged({ context: undefined }, getState, setState);
			}
		},
	setSurfaceMetadata:
		(surfaceMetadata: SurfaceMetadata) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateStateIfChanged({ surfaceMetadata }, getState, setState);
		},
	setProfileData:
		(profileData: ProfileData) =>
		({ setState }: StoreActionApi<AISmartButtonState>) => {
			setState({ profileData });
		},
	setAutomationsConfig:
		(automationsConfig: AutomationsConfig) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			updateStateIfChanged({ automationsConfig }, getState, setState);
		},
	removeAutomationsConfig:
		(sourceId: AISmartButtonContext['sourceId']) =>
		({ getState, setState }: StoreActionApi<AISmartButtonState>) => {
			const { automationsConfig } = getState();
			if (automationsConfig?.sourceId === sourceId) {
				updateStateIfChanged({ automationsConfig: undefined }, getState, setState);
			}
		},
	setShowConvoStarters:
		(showConvoStarters: boolean) =>
		({ setState }: StoreActionApi<AISmartButtonState>) => {
			setState({ showConvoStarters });
		},
};

export const AISmartButtonStore = createStore({
	initialState,
	actions,
	name: 'AISmartButtonStore',
});

export const useAISmartButtonActions = createActionsHook(AISmartButtonStore);
